import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Activity, AlertTriangle } from 'lucide-react';
import Header from '../components/Layout/Header';
import StatusPill from '../components/StatusPill';
import UptimeBar from '../components/UptimeBar';
import { monitorApi } from '../services/api';
import { APP_CONFIG, formatDate, formatDuration, formatUptime } from '../config/appConfig';
import { Monitor, MonitorCheck, MonitorEvent, UptimeBar as UptimeBarType } from '../types';

const Stat: React.FC<{ label: string; value: string; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-2 text-slate-400 text-xs font-medium">{icon}{label}</div>
    <div className="text-xl font-bold text-slate-800 mt-1">{value}</div>
  </div>
);

const MonitorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const monitorId = Number(id);
  const [monitor, setMonitor] = useState<Monitor | null>(null);
  const [checks, setChecks] = useState<MonitorCheck[]>([]);
  const [events, setEvents] = useState<MonitorEvent[]>([]);
  const [bars, setBars] = useState<UptimeBarType[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [m, c, e, b] = await Promise.all([
        monitorApi.getMonitor(monitorId),
        monitorApi.getMonitorChecks(monitorId, 60),
        monitorApi.getMonitorEvents(monitorId),
        monitorApi.getMonitorUptimeBars(monitorId, 30),
      ]);
      setMonitor(m.data);
      setChecks(c.data);
      setEvents(e.data);
      setBars(b.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [monitorId]);

  useEffect(() => {
    load();
    const t = setInterval(load, APP_CONFIG.REFRESH_INTERVAL);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return (<div className="min-h-screen"><Header /><div className="text-center py-20 text-slate-400">Loading…</div></div>);
  if (!monitor) return (<div className="min-h-screen"><Header /><div className="text-center py-20 text-slate-400">Monitor not found.</div></div>);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft size={16} /> Back to dashboard
        </Link>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{monitor.name}</h1>
            <div className="text-sm text-slate-400 mt-1"><code>{monitor.method}</code> {monitor.url}</div>
            {monitor.group_name && <div className="text-xs text-slate-400 mt-1">Group: {monitor.group_name}</div>}
          </div>
          <StatusPill status={monitor.current_status} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat label="24h Uptime" value={formatUptime(monitor.uptime_24h)} icon={<Activity size={14} />} />
          <Stat label="7d Uptime" value={formatUptime(monitor.uptime_7d)} icon={<Activity size={14} />} />
          <Stat label="30d Uptime" value={formatUptime(monitor.uptime_30d)} icon={<Activity size={14} />} />
          <Stat label="90d Uptime" value={formatUptime(monitor.uptime_90d)} icon={<Activity size={14} />} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-slate-700">Last 30 days availability</span>
            <span className="text-xs text-slate-400">Check every {formatDuration(monitor.interval_seconds)}</span>
          </div>
          <UptimeBar bars={bars} days={30} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Downtime history */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-slate-700 flex items-center gap-2">
              <AlertTriangle size={16} /> Downtime History
            </div>
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-auto">
              {events.length === 0 ? (
                <div className="px-5 py-8 text-center text-slate-400 text-sm">No downtime recorded 🎉</div>
              ) : events.map(e => (
                <div key={e.id} className="px-5 py-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-semibold flex items-center gap-2 ${e.resolved_at ? 'text-slate-600' : 'text-red-600'}`}>
                      <span className={`w-2 h-2 rounded-full ${e.resolved_at ? 'bg-slate-400' : 'bg-red-500'}`} />
                      {e.resolved_at ? 'Resolved' : 'Ongoing'} {e.status_code ? `· HTTP ${e.status_code}` : ''}
                    </span>
                    <span className="text-xs text-slate-400">{formatDuration(e.duration_seconds)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{e.root_cause}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{formatDate(e.started_at)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent checks */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-slate-700 flex items-center gap-2">
              <Clock size={16} /> Recent Checks
            </div>
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-auto">
              {checks.map(c => (
                <div key={c.id} className="px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${c.success ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-sm text-slate-600">{c.status_code ?? 'ERR'}</span>
                    {!c.success && c.error && <span className="text-xs text-red-400 truncate max-w-[160px]">{c.error}</span>}
                  </div>
                  <div className="text-xs text-slate-400">
                    {c.response_time_ms != null ? `${c.response_time_ms} ms · ` : ''}{formatDate(c.created_at)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitorDetailPage;
